import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import type { ResponsiveStyleValue } from '@mui/system';
import type React from 'react';
import { useEffect, useId, useState } from 'react';
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
  type UseFormTrigger,
} from 'react-hook-form';

import Icon, { IconName } from '@/components/Icon/Icon';

import useStyles from './Dropdown.styles';
import DropdownTextField from './DropdownTextField';

export enum DropdownSize {
  LG = 'lg',
  SM = 'sm',
}

interface DropdownProps<T extends FieldValues> {
  label: string;
  options: string[];
  // toolTip?: boolean; // TODO: PXE-1359
  onChange: (item: string) => void;
  disabled?: boolean;
  size?: ResponsiveStyleValue<DropdownSize>;
  error?: string;
  placeholder?: string;
  defaultOption?: string;
  hideLabel?: boolean;
  name?: Path<T>;
  trigger?: UseFormTrigger<T>;
  control?: Control<T>;
  classnames?: {
    label?: string;
    textField?: string;
  };
  isOnDark?: boolean;
  disablePortal?: boolean;
}

const Dropdown = <T extends FieldValues>({
  options,
  label,
  error,
  name,
  onChange,
  placeholder,
  control,
  trigger,
  classnames,
  disabled = false,
  size = DropdownSize.LG,
  defaultOption = '',
  hideLabel = false,
  isOnDark = false,
  disablePortal,
}: DropdownProps<T>) => {
  const toolTip = false; // Implement as part of PXE-1359
  const [option, setOption] = useState<string>(defaultOption);

  const { classes, cx } = useStyles({ size, isOnDark });
  const labelId = useId();

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setOption(event.target.value as string);
    onChange(event.target.value);
  };

  const selectOptions = options.map((option: string, index: number) => (
    <MenuItem
      key={`${option}-${
        // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
        index
      }`}
      value={option}
      divider={index !== options.length - 1}
      className={classes.menu}
      data-testid={`menu-item-${index}`}
    >
      {option}
    </MenuItem>
  ));

  useEffect(() => {
    setOption(defaultOption);
  }, [defaultOption]);

  const textFieldProps = {
    disabled,
    option,
    handleChange,
    placeholder,
    error,
    selectOptions,
    labelId,
    classes: {
      base: cx(classes.base, classnames?.textField, classes.responsiveSizes),
      placeholder: classes.placeholder,
      menu: classes.placeholder,
    },
    disablePortal,
  };

  return (
    <FormControl
      fullWidth
      className={cx(classes.wrapper, classnames?.textField)}
    >
      <FormLabel
        id={labelId}
        className={cx(
          classes.label,
          hideLabel && classes.labelHidden,
          classnames?.label && classnames.label
        )}
        disabled={disabled}
      >
        {label}
        {toolTip ? <Icon name={IconName.HELP} /> : ''}
      </FormLabel>
      {control && name ? (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <DropdownTextField
              field={field}
              trigger={trigger}
              {...textFieldProps}
            />
          )}
        />
      ) : (
        <DropdownTextField {...textFieldProps} />
      )}
    </FormControl>
  );
};

export default Dropdown;
